import React, { useEffect, useState, useRef } from 'react';
import { XIcon, UserIcon, AlertCircleIcon, MessageSquareIcon, ExternalLinkIcon, TrashIcon, CheckIcon, ClockIcon, FileTextIcon, EditIcon, SaveIcon, Trash2Icon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { updateAlert, deleteAlert } from '../../services/alertService';
import { toast } from 'react-hot-toast';
interface StudentAlertPanelProps {
  open: boolean;
  alert: {
    id: string;
    studentId: string;
    studentName: string;
    reason: string;
    practiceHours?: number;
    details?: string;
    createdAt?: any;
    status?: 'active' | 'dismissed';
  } | null;
  onClose: () => void;
  onDismiss: (alertId: string) => void;
  onMessage: (studentId: string) => void;
  onAlertUpdated?: () => void;
  onAlertDeleted?: (alertId: string) => void;
  darkMode: boolean;
}
export const StudentAlertPanel: React.FC<StudentAlertPanelProps> = ({
  open,
  alert,
  onClose,
  onDismiss,
  onMessage,
  onAlertUpdated,
  onAlertDeleted,
  darkMode
}) => {
  const navigate = useNavigate();
  const {
    currentUser
  } = useAuth();
  const panelRef = useRef<HTMLDivElement>(null);
  const [animation, setAnimation] = useState<'entering' | 'entered' | 'exiting' | 'exited'>('exited');
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedReason, setEditedReason] = useState('');
  const [editedDetails, setEditedDetails] = useState('');
  const [editedPracticeHours, setEditedPracticeHours] = useState<number | undefined>(undefined);
  // Local state for displayed values (updates after save)
  const [displayedReason, setDisplayedReason] = useState('');
  const [displayedDetails, setDisplayedDetails] = useState('');
  const [displayedPracticeHours, setDisplayedPracticeHours] = useState<number | undefined>(undefined);
  const lastActiveElement = useRef<HTMLElement | null>(null);
  // Handle animation states
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (open && animation !== 'entered') {
      lastActiveElement.current = document.activeElement as HTMLElement;
      setAnimation('entering');
      timer = setTimeout(() => {
        setAnimation('entered');
      }, 50);
    } else if (!open && animation !== 'exited') {
      setAnimation('exiting');
      timer = setTimeout(() => {
        setAnimation('exited');
      }, 300);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open, animation]);
  // Reset when panel closes
  useEffect(() => {
    if (!open) {
      setShowDismissConfirm(false);
      setIsDismissing(false);
      setShowDeleteConfirm(false);
      setIsDeleting(false);
      setIsEditing(false);
      setIsSaving(false);
    }
  }, [open]);
  // Initialize edit fields when alert changes
  useEffect(() => {
    if (alert) {
      setEditedReason(alert.reason || '');
      setEditedDetails(alert.details || '');
      setEditedPracticeHours(alert.practiceHours);
      // Also initialize displayed values
      setDisplayedReason(alert.reason || '');
      setDisplayedDetails(alert.details || '');
      setDisplayedPracticeHours(alert.practiceHours);
    }
  }, [alert]);
  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open, onClose]);
  // Restore focus when panel closes
  useEffect(() => {
    if (!open && lastActiveElement.current) {
      setTimeout(() => {
        lastActiveElement.current?.focus();
      }, 300);
    }
  }, [open]);
  // Focus trap inside the panel
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const panel = panelRef.current;
    const focusableElements = panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusableElements.length === 0) return;
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    if (animation === 'entered') {
      firstElement.focus();
    }
    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          event.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          event.preventDefault();
        }
      }
    };
    panel.addEventListener('keydown', handleTabKey);
    return () => {
      panel.removeEventListener('keydown', handleTabKey);
    };
  }, [open, animation]);
  if (animation === 'exited' && !open) return null;
  const handleDismissClick = async () => {
    if (!alert?.id) return;
    setIsDismissing(true);
    try {
      await onDismiss(alert.id);
      setShowDismissConfirm(false);
      onClose();
    } catch (error) {
      console.error('Error dismissing alert:', error);
    } finally {
      setIsDismissing(false);
    }
  };
  const handleMessageClick = () => {
    if (alert?.studentId) {
      onMessage(alert.studentId);
    }
  };
  const handleViewProfile = () => {
    if (alert?.studentId) {
      navigate('/my-students');
      onClose();
    }
  };
  const handleEditClick = () => {
    setIsEditing(true);
  };
  const handleCancelEdit = () => {
    // Restore original values
    if (alert) {
      setEditedReason(alert.reason || '');
      setEditedDetails(alert.details || '');
      setEditedPracticeHours(alert.practiceHours);
    }
    setIsEditing(false);
  };
  const handleSaveEdit = async () => {
    if (!alert?.id || !currentUser?.uid) return;
    setIsSaving(true);
    try {
      await updateAlert(currentUser.uid, alert.id, {
        reason: editedReason,
        details: editedDetails,
        practiceHours: editedPracticeHours
      });
      toast.success('Alert updated successfully');
      setIsEditing(false);
      // Update displayed values immediately
      setDisplayedReason(editedReason);
      setDisplayedDetails(editedDetails);
      setDisplayedPracticeHours(editedPracticeHours);
      // Notify parent to refresh alerts
      if (onAlertUpdated) {
        onAlertUpdated();
      }
    } catch (error) {
      console.error('Error updating alert:', error);
      toast.error('Failed to update alert. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  const handleDeleteClick = async () => {
    if (!alert?.id || !currentUser?.uid) return;
    setIsDeleting(true);
    try {
      await deleteAlert(currentUser.uid, alert.id);
      toast.success('Alert deleted permanently');
      setShowDeleteConfirm(false);
      // Notify parent to refresh alerts
      if (onAlertDeleted) {
        onAlertDeleted(alert.id);
      }
      onClose();
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast.error('Failed to delete alert. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };
  const formatDate = (dateStr?: any) => {
    if (!dateStr) return '—';
    let date: Date;
    if (dateStr.toDate && typeof dateStr.toDate === 'function') {
      date = dateStr.toDate();
    } else if (dateStr instanceof Date) {
      date = dateStr;
    } else {
      date = new Date(dateStr);
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const handlePanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  return <div className={`fixed inset-0 z-50 ${animation === 'exited' ? 'pointer-events-none' : ''}`} aria-modal="true" role="dialog" aria-labelledby="alert-panel-title" data-id="element-473">
      {/* Overlay */}
      <div className={`absolute inset-0 bg-black transition-opacity duration-300 ${animation === 'entering' || animation === 'entered' ? 'opacity-50' : 'opacity-0'}`} onClick={e => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }} data-id="element-474" />

      {/* Panel */}
      <div ref={panelRef} className={`absolute inset-y-0 right-0 w-full max-w-[480px] ${darkMode ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'} shadow-xl flex flex-col transform transition-transform duration-500 ${animation === 'entering' || animation === 'entered' ? 'translate-x-0' : 'translate-x-full'} md:rounded-l-2xl`} onClick={handlePanelClick} data-id="element-475">
        {/* Panel Header */}
        <div className={`px-6 py-4 flex items-center justify-between border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-476">
          <div className="flex items-center gap-3" data-id="element-477">
            <div className={`p-2 rounded-lg ${darkMode ? 'bg-red-500/20' : 'bg-red-100'}`} data-id="element-478">
              <AlertCircleIcon className="h-5 w-5 text-red-500" data-id="element-479" />
            </div>
            <h2 id="alert-panel-title" className="text-xl font-semibold truncate flex-1" data-id="element-480">
              Student Alert
            </h2>
          </div>
          <button type="button" onClick={onClose} className={`p-2 rounded-full ${darkMode ? 'hover:bg-navy-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'} transition-colors`} aria-label="Close panel" data-id="element-481">
            <XIcon className="h-5 w-5" data-id="element-482" />
          </button>
        </div>

        {/* Dismiss Confirmation */}
        {showDismissConfirm && <div className="p-4 mx-6 mt-6 rounded-lg bg-red-500/10 border border-red-500/30" data-id="element-483">
            <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-800'} mb-3`} data-id="element-484">
              <AlertCircleIcon className="inline-block h-4 w-4 mr-2 text-red-500" data-id="element-485" />
              Dismiss this alert? You can view dismissed alerts in the alert
              history.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-486">
              <button type="button" onClick={() => setShowDismissConfirm(false)} disabled={isDismissing} className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} ${isDismissing ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-487">
                Cancel
              </button>
              <button type="button" onClick={handleDismissClick} disabled={isDismissing} className={`px-3 py-1.5 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 flex items-center gap-1 ${isDismissing ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-488">
                {isDismissing ? <>
                    <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" data-id="element-489" />
                    Dismissing...
                  </> : <>
                    <CheckIcon className="h-3 w-3" data-id="element-490" />
                    Dismiss
                  </>}
              </button>
            </div>
          </div>}

        {/* Delete Confirmation */}
        {showDeleteConfirm && <div className="p-4 mx-6 mt-6 rounded-lg bg-red-500/10 border border-red-500/30" data-id="element-491">
            <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-800'} mb-3`} data-id="element-492">
              <Trash2Icon className="inline-block h-4 w-4 mr-2 text-red-500" data-id="element-493" />
              Delete this alert forever? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-494">
              <button type="button" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting} className={`px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-495">
                Cancel
              </button>
              <button type="button" onClick={handleDeleteClick} disabled={isDeleting} className={`px-3 py-1.5 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 flex items-center gap-1 ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-496">
                {isDeleting ? <>
                    <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" data-id="element-497" />
                    Deleting...
                  </> : <>
                    <Trash2Icon className="h-3 w-3" data-id="element-498" />
                    Delete Forever
                  </>}
              </button>
            </div>
          </div>}

        {/* Panel Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-499">
          {alert ? <div className="p-6 space-y-6" data-id="element-500">
              {/* Student Section */}
              <div data-id="element-501">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-502">
                  Student
                </h3>
                <div className="flex items-center" data-id="element-503">
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center mr-3 ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-504">
                    <UserIcon className="h-6 w-6 text-[#3B82F6]" data-id="element-505" />
                  </div>
                  <div data-id="element-506">
                    <p className={`font-medium text-lg ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-507">
                      {alert.studentName}
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-508">
                      Student ID: {alert.studentId}
                    </p>
                  </div>
                </div>
              </div>

              {/* Alert Reason */}
              <div className={`pb-6 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-509">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-510">
                  Alert Reason
                </h3>
                {isEditing ? <input type="text" value={editedReason} onChange={e => setEditedReason(e.target.value)} className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-navy-900 border-red-500/50 text-white placeholder-gray-400' : 'bg-white border-red-300 text-gray-900'} focus:ring-2 focus:ring-red-500 focus:border-transparent`} placeholder="Enter alert reason" data-id="element-511" /> : <div className={`p-4 rounded-lg ${darkMode ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'}`} data-id="element-512">
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-513">
                      {displayedReason}
                    </p>
                  </div>}
              </div>

              {/* Practice Hours */}
              {(displayedPracticeHours !== undefined || isEditing) && <div className={`pb-6 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-514">
                  <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-515">
                    Practice Hours
                  </h3>
                  {isEditing ? <div className="flex items-center gap-3" data-id="element-516">
                      <input type="number" value={editedPracticeHours || ''} onChange={e => setEditedPracticeHours(e.target.value ? parseFloat(e.target.value) : undefined)} step="0.5" min="0" className={`flex-1 px-4 py-3 rounded-lg border ${darkMode ? 'bg-navy-900 border-navy-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-orange-500 focus:border-transparent`} placeholder="Hours per week" data-id="element-517" />
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-518">
                        hrs/week
                      </span>
                    </div> : <div className="flex items-center" data-id="element-519">
                      <ClockIcon className="h-5 w-5 mr-3 text-[#3B82F6]" data-id="element-520" />
                      <div data-id="element-521">
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-522">
                          {displayedPracticeHours} hrs/week
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-523">
                          Current practice level
                        </p>
                      </div>
                    </div>}
                </div>}

              {/* Details */}
              {(displayedDetails || isEditing) && <div className={`pb-6 border-b ${darkMode ? 'border-navy-700' : 'border-gray-200'}`} data-id="element-524">
                  <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-525">
                    Details
                  </h3>
                  {isEditing ? <textarea value={editedDetails} onChange={e => setEditedDetails(e.target.value)} rows={4} className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-navy-900 border-navy-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none`} placeholder="Add additional details or notes" data-id="element-526" /> : <div className="flex items-start" data-id="element-527">
                      <FileTextIcon className="h-5 w-5 mr-3 mt-0.5 text-[#8B5CF6]" data-id="element-528" />
                      <p className={`whitespace-pre-line ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-529">
                        {displayedDetails}
                      </p>
                    </div>}
                </div>}

              {/* Meta Information */}
              <div data-id="element-530">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-531">
                  Alert Information
                </h3>
                <div className="space-y-3" data-id="element-532">
                  {alert.createdAt && <div className="flex items-start" data-id="element-533">
                      <ClockIcon className="h-4 w-4 mr-2 mt-0.5 text-[#8B5CF6]" data-id="element-534" />
                      <div data-id="element-535">
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-536">
                          Created
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-537">
                          {formatDate(alert.createdAt)}
                        </p>
                      </div>
                    </div>}
                </div>
              </div>
            </div> : <div className="p-6 text-center" data-id="element-538">
              <p className={darkMode ? 'text-gray-400' : 'text-gray-500'} data-id="element-539">
                No alert data available
              </p>
            </div>}
        </div>

        {/* Panel Footer - Quick Actions */}
        {alert && <div className={`p-6 border-t ${darkMode ? 'border-navy-700' : 'border-gray-200'} space-y-3`} data-id="element-540">
            {isEditing ? <>
                {/* Save Button */}
                <button type="button" onClick={handleSaveEdit} disabled={isSaving || !editedReason.trim()} className={`w-full px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors bg-[#10B981] text-white hover:bg-[#059669] ${isSaving || !editedReason.trim() ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-541">
                  {isSaving ? <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-542" />
                      Saving...
                    </> : <>
                      <SaveIcon className="h-4 w-4" data-id="element-543" />
                      Save Changes
                    </>}
                </button>

                {/* Cancel Button */}
                <button type="button" onClick={handleCancelEdit} disabled={isSaving} className={`w-full px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-544">
                  <XIcon className="h-4 w-4" data-id="element-545" />
                  Cancel
                </button>
              </> : alert.status === 'dismissed' ? <>
                {/* Delete Forever (Dismissed Alerts Only) */}
                <button type="button" onClick={() => setShowDeleteConfirm(true)} className={`w-full px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${darkMode ? 'text-red-400 hover:bg-red-900/20 border border-red-500/30' : 'text-red-600 hover:bg-red-50 border border-red-200'}`} data-id="element-546">
                  <Trash2Icon className="h-4 w-4" data-id="element-547" />
                  Delete Forever
                </button>
              </> : <>
                {/* Edit Alert */}
                <button type="button" onClick={handleEditClick} className={`w-full px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-548">
                  <EditIcon className="h-4 w-4" data-id="element-549" />
                  Edit Alert
                </button>

                {/* Dismiss Alert */}
                <button type="button" onClick={() => setShowDismissConfirm(true)} className={`w-full px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${darkMode ? 'text-red-400 hover:bg-red-900/20 border border-red-500/30' : 'text-red-600 hover:bg-red-50 border border-red-200'}`} data-id="element-550">
                  <XIcon className="h-4 w-4" data-id="element-551" />
                  Dismiss Alert
                </button>
              </>}
          </div>}
      </div>
    </div>;
};